import { Component, Input, ViewChildren, AfterViewInit, OnInit } from '@angular/core';
import gsap from 'gsap';
import { RemoteButtonKey, RemoteButtonKeyCode } from 'src/app/data/type/remote-button';
import { RemoteControlService } from 'src/app/service/remote-control.service';
import { LocalizationService } from 'src/app/service/localization.service';
import { getRemoteControlCopy } from 'src/app/utils/localization';
import { ParameterService } from 'src/app/service/parameter.service';
import { Parameter } from 'src/app/data/type/parameter';
import { TvFeature } from 'src/app/data/enum/tv-feature';
import isMobile from 'src/app/utils/is-mobile';
import { environment } from 'src/environments/environment';
import { Brand } from 'src/app/data/enum/brand';

interface RemoteControlButton {
  isActive: boolean;
  key: RemoteButtonKeyCode;
  ariaLabel: string;
}
@Component({
  selector: 'app-c02-remote-control-buttons',
  templateUrl: './c02-remote-control-buttons.component.html',
  styleUrls: [
    './c02-remote-control-buttons.component.scss',
    './c02-remote-control-buttons-landscape.component.scss',
  ],
})
export class C02RemoteControlButtonsComponent implements AfterViewInit, OnInit {
  @ViewChildren('controlButtonsRef') controlButtonsRef;
  @Input() featureName: TvFeature;
  @Input() tooltipCopy: string = '';
  @Input() disabled: boolean = false;
  @Input() set stepIndex(value: number) {
    this.tooltipClassName = `${this.featureName.toLowerCase()}-${value}`;
  }
  @Input() set buttons(value: Array<RemoteButtonKeyCode>) {
    this.showVoiceButton = value.includes(RemoteButtonKey.voice);

    const isHisense = this.parameterService.isHisenseBrand();

    this.directionalButtons = this.directionalButtons.reduce(
      (_directionalButtons, currentDirectionButton, index) => {
        if (
          currentDirectionButton.key !== RemoteButtonKey.center &&
          currentDirectionButton.key !== RemoteButtonKey['center-hisense']
        ) {
          _directionalButtons.push({
            ...currentDirectionButton,
            isActive: value.includes(this.directionalButtons[index].key),
          });
          return _directionalButtons;
        } else if (
          (isHisense && currentDirectionButton.key === RemoteButtonKey['center-hisense']) ||
          (!isHisense && currentDirectionButton.key === RemoteButtonKey.center)
        ) {
          _directionalButtons.push({
            ...currentDirectionButton,
            isActive: value.includes(this.directionalButtons[index].key),
          });
          return _directionalButtons;
        }

        return _directionalButtons;
      },
      [],
    );

    setTimeout(() => this.controlButtonsElement[0].focus());
  }

  directionalButtons: Array<RemoteControlButton> = [
    { isActive: false, key: RemoteButtonKey.center, ariaLabel: '' },
    { isActive: false, key: RemoteButtonKey['center-hisense'], ariaLabel: '' },
    { isActive: false, key: RemoteButtonKey.up, ariaLabel: '' },
    { isActive: false, key: RemoteButtonKey.right, ariaLabel: '' },
    { isActive: false, key: RemoteButtonKey.down, ariaLabel: '' },
    { isActive: false, key: RemoteButtonKey.left, ariaLabel: '' },
  ];

  showVoiceButton: boolean = false;
  private isInside: boolean = false;
  private controlButtonsElement: Array<HTMLElement> | null;

  public tooltipClassName = '';
  public isSttEnabled = this.parameterService.getParam(Parameter['disable-stt']) !== 'true';
  public baseHref = environment.baseHref;
  public isAbstractVersion = false;
  public remoteImageSrc = `${this.baseHref}/assets/images/abstract-remote/remote-control${
    this.parameterService.getParam(Parameter.brand) === Brand.hisense ? '-hisense' : '-abstract'
  }.png`;

  constructor(
    private localizationService: LocalizationService,
    private remoteControlService: RemoteControlService,
    private parameterService: ParameterService,
  ) {
    const buttonsCopy = getRemoteControlCopy(this.localizationService);
    this.directionalButtons.forEach((button) => {
      button.ariaLabel = buttonsCopy[button.key];
    });
  }

  ngOnInit(): void {
    this.isAbstractVersion = this.parameterService.isAbstractVersion();
  }

  ngAfterViewInit(): void {
    this.controlButtonsElement = this.controlButtonsRef.map(
      (buttonsRef) => buttonsRef.nativeElement,
    );
  }

  onComponentMouseEnter(): void {
    if (isMobile() || this.isAbstractVersion) return;

    this.isInside = true;
    gsap.getById('control-buttons')?.pause();
    gsap.to(`.remote-control-tooltip`, { autoAlpha: 0, x: 30, duration: 0.4 });
  }

  onComponentMouseLeave(): void {
    if (isMobile()) return;

    this.isInside = false;
    gsap.to(`.remote-control-tooltip`, { autoAlpha: 1, x: 0, duration: 0.4 });
  }

  onButtonMouseOver(event: Event) {
    if (isMobile() || this.isAbstractVersion) return;

    const eventTarget = event.target as HTMLButtonElement;
    const assetTarget = eventTarget.dataset.direction;

    gsap.to(`.remote-asset.button`, { opacity: 0.5 });
    gsap.to(`.asset-${assetTarget}`, { opacity: 1 });
  }

  onButtonMouseLeave() {
    if (isMobile() || this.isAbstractVersion) return;

    gsap.to('.remote-asset.button', {
      opacity: 0.5,
      onComplete: () => {
        if (!this.isInside) gsap.getById('control-buttons').restart();
      },
    });
    gsap.to('.remote-asset.button', { opacity: 0.5 });
  }

  onButtonClick(key: RemoteButtonKeyCode) {
    this.remoteControlService.triggerEvent(key);
  }

  identify(_: number, item: RemoteControlButton) {
    return item.key;
  }
}
